<template>
  <div class="app-container" style="padding-top: 5px">
    <!--       default-expand-all -->
    <el-tree
      ref="treeRef"
      :data="permissionList"
      :props="props"
      icon-class="el-icon-thumb"
      node-key="id"
      show-checkbox
    />
  </div>
</template>

<script>
import { getPermissionStructureByAlert, getPermissionStructureByField } from '@/api/role';

export default {
  name: 'PermissionTree',
  props: {
    permissionTreePop: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      props: {
        label: 'name',
        children: 'children',
        disabled: 'disabled'
      },
      // 权限树
      permissionList: [],
      // 选中了的节点
      selectedPermissionList: [],
      // 默认选中节点
      defSelectedPermissionList: [],
      reminderTip: false
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    /**
     * 递归标记角色权限节点为 disabled（只读）
     * 仅当当前节点及其整棵子树都属于角色权限时，父节点才禁用
     * @param {Object} node 权限树节点
     * @param {Set} roleIdSet 角色权限 ID 集合
     */
    markRoleNodes(node, roleIdSet) {
      if (!node) return node;
      const children = node.children && node.children.length > 0
        ? node.children.map(child => this.markRoleNodes(child, roleIdSet))
        : [];
      const isRolePermission = roleIdSet.has(node.id) || roleIdSet.has(String(node.id));
      const hasChildren = children.length > 0;
      const allChildrenDisabled = hasChildren && children.every(child => child.disabled);
      const disabled = hasChildren ? isRolePermission && allChildrenDisabled : isRolePermission;
      return {
        ...node,
        disabled,
        children
      };
    },
    collectSelectedNodes(nodes, selectedIdSet) {
      if (!nodes || !nodes.length) {
        return [];
      }
      const selectedNodes = [];
      const visit = (node) => {
        const children = node.children || [];
        const hasSelectedChild = children.map(visit).some(Boolean);
        const isSelected = selectedIdSet.has(node.id) || selectedIdSet.has(String(node.id));
        if (isSelected || hasSelectedChild) {
          selectedNodes.push(node);
          return true;
        }
        return false;
      };
      nodes.forEach(visit);
      return selectedNodes;
    },
    async init() {
      // 1. 初始化所有数据，兜底空数组，避免undefined
      this.permissionList = [];
      this.defSelectedPermissionList = []; // 提前初始化，保证响应式
      const lastChildrenIdsConcat = [];
      const appendLastChildrenIds = (ids) => {
        if (Array.isArray(ids) && ids.length > 0) {
          lastChildrenIdsConcat.push(...ids);
        }
      };

      // 角色权限 ID 集合（用于标记只读节点）
      const roleCharIds = this.permissionTreePop?.rolePermissionIds?.char || [];
      const roleAlertIds = this.permissionTreePop?.rolePermissionIds?.alert || [];
      const roleIdSet = new Set([...roleCharIds, ...roleAlertIds, ...roleCharIds.map(String), ...roleAlertIds.map(String)]);

      try {
        // 第一个接口：获取字段权限结构
        const res = await getPermissionStructureByField();
        if (res.code === 200 && res.data?.permissionStructure) {
          const rootNode = res.data.permissionStructure[0];
          // 标记角色权限节点为 disabled
          this.permissionList.push(this.markRoleNodes(rootNode, roleIdSet));
          appendLastChildrenIds(res.data.lastChildrenIds);
        }

        // 第二个接口：获取告警权限结构
        const resTip = await getPermissionStructureByAlert();
        if (resTip.code === 200 && resTip.data?.permissionStructure) {
          const rootNode = resTip.data.permissionStructure[0];
          // 标记角色权限节点为 disabled
          this.permissionList.push(this.markRoleNodes(rootNode, roleIdSet));
          appendLastChildrenIds(resTip.data.lastChildrenIds);

          // 2. 筛选有效勾选id + 统一类型（核心修复）
          if (Array.isArray(this.permissionTreePop?.selectedPermissionList?.char)) {
            this.defSelectedPermissionList = this.permissionTreePop.selectedPermissionList.char
              .filter((item) => {
                // 双重校验：id存在 + 非空
                return item && lastChildrenIdsConcat.indexOf(item) !== -1;
              })
              .map((item) => {
                // ✅ 关键：统一id类型！和树节点的id类型保持一致（二选一）
                // ▶️ 如果树节点id是【数字】，打开下面注释，关闭字符串行
                // return Number(item);
                // ▶️ 如果树节点id是【字符串】，保留这行
                return String(item);
              });
          }
        }

        // 3. ✅ 核心关键：异步数据加载完成后，手动调用树组件勾选API（替代失效的default-checked-keys）
        this.$nextTick(() => {
          if (this.$refs.treeRef && this.defSelectedPermissionList.length) {
            // 清除原有勾选，避免残留
            this.$refs.treeRef.setCheckedKeys([]);
            // 设置新的勾选项，100%生效！
            this.$refs.treeRef.setCheckedKeys(this.defSelectedPermissionList);
            console.log('最终勾选的ID：', this.defSelectedPermissionList);
          }
        });
      } catch (err) {
        console.error('权限数据加载失败：', err);
      }
    },
    getSelectedPermissionList() {
      const checkedKeys = this.$refs.treeRef.getCheckedKeys();
      const halfCheckedKeys = this.$refs.treeRef.getHalfCheckedKeys();
      const selectedIdSet = new Set([
        ...checkedKeys,
        ...halfCheckedKeys,
        ...checkedKeys.map(String),
        ...halfCheckedKeys.map(String)
      ]);
      const selectedNodes = this.collectSelectedNodes(this.permissionList, selectedIdSet);
      const menuPermissions = [];
      const buttonPermissions = [];
      const permissionIds = [];
      const addedPermissionIds = new Set();
      const addedPermissionCodes = new Set();
      selectedNodes.forEach((item) => {
        if (item.id !== undefined && item.id !== null && !addedPermissionIds.has(item.id)) {
          addedPermissionIds.add(item.id);
          permissionIds.push(item.id);
        }
        if (!item.code || addedPermissionCodes.has(item.code)) {
          return;
        }
        addedPermissionCodes.add(item.code);
        const code = item.code.split(':')[0];
        if (code === 'menu') {
          menuPermissions.push(item.code);
        } else {
          buttonPermissions.push(item.code);
        }
      });

      return {
        permissionIds,
        menuPermissions,
        buttonPermissions
      };
    }
  }
};
</script>

<style scoped>
.el-tree {
  min-width: 100%;
  display: inline-block;
}

.tree {
  overflow: auto;
  max-height: 400px;
}

/* 角色权限节点样式：灰色勾选，不可操作 */
::v-deep .el-tree-node__content .el-checkbox.is-disabled .el-checkbox__inner {
  background-color: #c0c4cc;
  border-color: #c0c4cc;
}

::v-deep .el-tree-node__content .el-checkbox.is-disabled {
  cursor: not-allowed;
}

::v-deep .el-tree-node__content .el-checkbox.is-disabled + span {
  color: #909399;
}
</style>
